import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { api, eur, CATEGORY_LABELS } from "@/lib/api";
import { CATEGORIES } from "@/lib/data";
import { useCart } from "@/context/CartContext";
import { useOrderedQuantities } from "@/lib/useOrderedQuantities";
import { pressZoom } from "@/lib/usePressZoom";
import ImageZoom from "@/components/ImageZoom";
import OfferBanner from "@/components/OfferBanner";
import DiscountNote from "@/components/DiscountNote";
import WineDetail from "@/components/WineDetail";
import ProducerLeaflet from "@/components/ProducerLeaflet";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";

const FILTERS = [{ id: "", label: "Tous" }, ...CATEGORIES.map((c) => ({ id: c.id, label: c.label }))];

function WineRow({ wine, onSelect, orderedQty, onZoom }) {
  const { items, add, setQty } = useCart();
  const inCartQty = items.find((i) => i.wine.id === wine.id)?.qty || 0;
  const out = wine.stock <= 0;

  return (
    <li
      onClick={() => onSelect(wine)}
      className="flex items-center gap-3 sm:gap-5 py-4 border-b border-[#6B1226]/10 cursor-pointer group"
      data-testid="wine-row"
    >
      {wine.image ? (
        <img
          src={wine.image}
          alt={wine.name}
          loading="lazy"
          {...pressZoom(() => onZoom(wine.image, true))}
          onClick={(e) => {
            e.stopPropagation();
            onZoom(wine.image, false);
          }}
          className="h-24 sm:h-28 w-auto object-contain no-frame shrink-0 cursor-zoom-in bottle-hover"
          data-testid="wine-row-image"
        />
      ) : (
        <div className="h-24 sm:h-28 w-10 shrink-0" aria-hidden="true" />
      )}
      <div className="flex-1 min-w-0">
        <p className="font-eyebrow text-[0.55rem] tracking-[0.25em] uppercase text-[#C5A059]">
          {CATEGORY_LABELS[wine.category] || wine.category}
          {wine.year ? ` · ${wine.year}` : ""}
        </p>
        <h3 className="font-display text-lg sm:text-xl font-semibold text-[#1C1917] leading-snug group-hover:text-[#6B1226] transition-colors" data-testid="wine-row-title">
          {wine.name}
        </h3>
        <p className="text-xs text-[#78716C] mt-0.5">
          {[wine.appellation, wine.region].filter(Boolean).join(" · ")}
        </p>
        <DiscountNote producer={wine.producer} compact />
        {orderedQty > 0 && (
          <p className="text-xs font-semibold text-[#8C6D23] mt-1" data-testid="wine-row-ordered-qty">
            Déjà commandé : {orderedQty} bouteille{orderedQty > 1 ? "s" : ""}
          </p>
        )}
        {out && <p className="text-xs font-semibold text-stone-400 mt-1 uppercase tracking-widest">Épuisé</p>}
      </div>
      <div className="shrink-0 flex flex-col items-end gap-2">
        <span className="price text-xl sm:text-2xl text-[#6B1226]" data-testid="wine-row-price">
          {eur(wine.price)}
        </span>
        {inCartQty > 0 ? (
          <span
            className="inline-flex items-center gap-2 bg-[#6B1226]/10 border border-[#6B1226]/25 rounded-full px-2 py-1"
            data-testid="wine-row-qty-badge"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setQty(wine.id, inCartQty - 1);
              }}
              className="text-[#6B1226] hover:bg-[#6B1226]/10 rounded-full p-0.5"
              data-testid="wine-row-qty-minus"
              aria-label="Retirer une bouteille"
            >
              <Minus size={12} />
            </button>
            <span className="text-xs font-bold text-[#6B1226] whitespace-nowrap">{inCartQty} dans ma commande</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setQty(wine.id, inCartQty + 1);
              }}
              className="text-[#6B1226] hover:bg-[#6B1226]/10 rounded-full p-0.5"
              data-testid="wine-row-qty-plus"
              aria-label="Ajouter une bouteille"
            >
              <Plus size={12} />
            </button>
          </span>
        ) : (
          <button
            type="button"
            disabled={out}
            onClick={(e) => {
              e.stopPropagation();
              if (out) return;
              add(wine, 1);
              toast.success(`${wine.name} ajouté au panier`);
            }}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold tracking-wide transition-colors ${
              out ? "bg-stone-200 text-stone-400 cursor-not-allowed" : "bg-[#6B1226] text-[#FAF6F0] hover:bg-[#580E1D]"
            }`}
            data-testid="wine-row-add-button"
          >
            <Plus size={14} /> Ajouter
          </button>
        )}
      </div>
    </li>
  );
}

export default function Catalogue() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "";
  const wineId = params.get("wine");
  const producer = params.get("producer") || "";
  const [wines, setWines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [zoomSrc, setZoomSrc] = useState(null);
  const [zoomHover, setZoomHover] = useState(false);
  const [producers, setProducers] = useState([]);
  const orderedMap = useOrderedQuantities();

  // Vignerons présents à la cave, avec leurs familles (pour filtrer les puces selon la catégorie)
  useEffect(() => {
    api
      .get("/wines")
      .then((res) => {
        const map = new Map();
        res.data.forEach((w) => {
          if (!w.producer) return;
          if (!map.has(w.producer)) map.set(w.producer, new Set());
          map.get(w.producer).add(w.category);
        });
        setProducers([...map.entries()].map(([name, cats]) => ({ name, cats })));
      })
      .catch(() => setProducers([]));
  }, []);

  // Toutes les maisons (vignette d'offre) vs celles de la catégorie affichée (puces de filtre)
  const allProducerNames = useMemo(() => producers.map((p) => p.name), [producers]);
  const producersInCat = useMemo(
    () => producers.filter((p) => !cat || p.cats.has(cat)).map((p) => p.name),
    [producers, cat]
  );

  // Changer de famille retire le filtre vigneron s'il n'a aucun vin dans cette famille
  const selectCat = (id) => {
    const keep = id && producer && producers.some((p) => p.name === producer && p.cats.has(id));
    setFilter({ cat: id || "", producer: keep ? producer : "" });
  };

  const setFilter = (patch) => {
    const next = {};
    const merged = { cat, producer, ...patch };
    if (merged.cat) next.cat = merged.cat;
    if (merged.producer) next.producer = merged.producer;
    setParams(next);
  };

  const openZoom = (src, viaHover) => {
    setZoomHover(viaHover);
    setZoomSrc(src);
  };

  useEffect(() => {
    if (wineId) {
      api.get(`/wines/${wineId}`).then((res) => setSelected(res.data)).catch(() => {});
    }
  }, [wineId]);

  const closeDetail = () => {
    setSelected(null);
    if (wineId) setParams(cat ? { cat } : {});
  };

  useEffect(() => {
    setLoading(true);
    const query = {};
    if (cat) query.category = cat;
    if (q) query.q = q;
    api
      .get("/wines", { params: query })
      .then((res) => setWines(res.data))
      .catch(() => setWines([]))
      .finally(() => setLoading(false));
  }, [cat, q]);

  const heading = useMemo(() => {
    const found = CATEGORIES.find((c) => c.id === cat);
    return found ? found.label : "Toute la cave";
  }, [cat]);

  const groups = useMemo(() => {
    const map = new Map();
    wines
      .filter((w) => !producer || w.producer === producer)
      .forEach((w) => {
        const key = w.producer || "Maison non précisée";
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(w);
      });
    return [...map.entries()];
  }, [wines, producer]);

  return (
    <div data-testid="catalogue-page">
      <section className="app-container px-4 sm:px-8 pt-10 sm:pt-14 pb-14">
        <Reveal>
          <p className="eyebrow mb-3">La cave du Bistroquet</p>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold text-[#1C1917] tracking-tight" data-testid="catalogue-title">
            {heading}
          </h1>
        </Reveal>

        <Reveal delay={0.05} className="mt-7">
          <OfferBanner producers={allProducerNames} onSelect={(p) => setFilter({ producer: p, cat: "" })} />
        </Reveal>

        <Reveal delay={0.1} className="mt-7 space-y-5">
          <div className="relative max-w-md">
            <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C5A059]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher une bouteille…"
              className="w-full bg-white border border-[#6B1226]/15 rounded-full pl-11 pr-4 py-3 text-base outline-none focus:border-[#6B1226]/50 transition-colors"
              data-testid="catalogue-search-input"
            />
          </div>

          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="catalogue-filters">
            {FILTERS.map((f) => (
              <button
                key={f.id || "all"}
                onClick={() => selectCat(f.id || "")}
                className={`shrink-0 rounded-full px-5 py-2.5 text-xs font-semibold tracking-wide transition-colors ${
                  cat === f.id
                    ? "bg-[#6B1226] text-[#FAF6F0]"
                    : "bg-white border border-[#6B1226]/15 text-[#57534E] hover:text-[#6B1226] hover:border-[#6B1226]/40"
                }`}
                data-testid={`filter-${f.id || "tous"}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {(producersInCat.length > 1 || (cat && producersInCat.length === 1)) && (
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="catalogue-producer-filters">
              <button
                onClick={() => setFilter({ producer: "" })}
                className={`shrink-0 rounded-full px-5 py-2.5 text-xs font-semibold tracking-wide transition-colors ${
                  !producer
                    ? "bg-[#C5A059] text-[#1C1917]"
                    : "bg-white border border-[#C5A059]/40 text-[#8C6D23] hover:border-[#C5A059]"
                }`}
                data-testid="producer-filter-tous"
              >
                {cat ? "Toutes les maisons" : "Tous les vignerons"}
              </button>
              {producersInCat.map((p) => (
                <button
                  key={p}
                  onClick={() => setFilter({ producer: p })}
                  className={`shrink-0 rounded-full px-5 py-2.5 text-xs font-semibold tracking-wide transition-colors ${
                    producer === p
                      ? "bg-[#C5A059] text-[#1C1917]"
                      : "bg-white border border-[#C5A059]/40 text-[#8C6D23] hover:border-[#C5A059]"
                  }`}
                  data-testid={`producer-filter-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </Reveal>

        {loading ? (
          <div className="py-24 text-center" data-testid="catalogue-loading">
            <p className="font-display text-2xl text-[#6B1226] italic">On descend à la cave…</p>
          </div>
        ) : wines.length === 0 ? (
          <div className="py-24 text-center" data-testid="catalogue-empty">
            {cat && !q ? (
              <>
                <p className="font-display text-2xl text-[#1C1917]">{heading} : bientôt à la carte</p>
                <p className="text-sm text-[#57534E] mt-3 max-w-xs mx-auto leading-relaxed">
                  Nous vous informerons prochainement de l'ensemble de nos arrivages.
                </p>
              </>
            ) : (
              <>
                <p className="font-display text-2xl text-[#1C1917]">Aucune bouteille trouvée</p>
                <p className="text-sm text-[#57534E] mt-2">Essayez une autre recherche ou catégorie.</p>
              </>
            )}
          </div>
        ) : (
          groups.map(([producer, list]) => (
            <section key={producer} className="mt-10" data-testid="producer-group">
              <Reveal>
                <ProducerLeaflet producer={producer} onClick={() => setFilter({ producer })} />
                <div className="flex items-baseline gap-2.5 border-b-2 border-[#6B1226]/20 pb-2 mt-4">
                  <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#1C1917]">{producer}</h2>
                  <span className="font-eyebrow text-[0.6rem] tracking-[0.25em] uppercase text-[#C5A059]">(viticulteur)</span>
                </div>
              </Reveal>
              <ul data-testid="producer-wine-list">
                {list.map((wine) => (
                  <WineRow key={wine.id} wine={wine} onSelect={setSelected} orderedQty={orderedMap[wine.id] || 0} onZoom={openZoom} />
                ))}
              </ul>
            </section>
          ))
        )}
      </section>
      <Footer />
      <WineDetail
        wine={selected}
        onClose={closeDetail}
        onDeleted={(id) => setWines((ws) => ws.filter((w) => w.id !== id))}
      />
      <ImageZoom src={zoomSrc} alt="Bouteille" viaHover={zoomHover} onStick={() => setZoomHover(false)} onClose={() => setZoomSrc(null)} />
    </div>
  );
}
